"use client"

import { useState, useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import {
  X, Check, Upload, Layers, AlertCircle, FileArchive, ChevronDown, Loader2, ScanSearch,
} from "lucide-react"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"
import { useToast } from "@/lib/hooks/use-toast"
import {
  createSkill,
  updateSkill,
  parseSkillByUrl,
  type CreateSkillRequest,
  type SkillVersionResp,
} from "@/lib/skill/skill-api"
import { uploadFile } from "@/lib/api/upload"
import { analytics } from "@/src/utils/analytics"

/* ── 支持的压缩包类型 ── */
const ARCHIVE_ACCEPTED_TYPES = [
  ".zip",
]
const ARCHIVE_ACCEPT_ATTR = ".zip,application/zip"
const ARCHIVE_MAX_SIZE = 2 * 1024 * 1024 // 2 MB

/* ── 技能分类选项 ── */
const CATEGORY_OPTIONS = [
  { value: "dev", label: "开发工具" },
  { value: "automation", label: "自动化" },
  { value: "data", label: "数据分析" },
  { value: "ai", label: "AI & 模型" },
  { value: "productivity", label: "效率工具" },
  { value: "other", label: "其他" },
]

/* ── 发布者类型选项 ── */
const PUBLISHER_TYPE_OPTIONS = [
  { value: "official", label: "官方" },
  { value: "individual", label: "个人" },
  { value: "organization", label: "组织" },
]

export interface SkillPublishEditData {
  id: number
  skillKey?: string
  version?: string
  name?: string
  description?: string
  category?: string
  tags?: string
  level?: string
  source?: string
  author?: string
  repoUrl?: string
  archivePath?: string
  directory?: string
  isPaid?: number
  price?: number
  publisherType?: string
  metadata?: Record<string, unknown>
  content?: string
}
export function SkillPublishModal({
  open,
  onClose,
  onSuccess,
  editSkill,
}: {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  editSkill?: SkillPublishEditData | null
}) {
  const { closing, requestClose, reset } = useAnimatedClose(onClose, 280)
  const [entered, setEntered] = useState(false)
  const toast = useToast()

  /* ── Form fields ── */
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState("")
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [source, setSource] = useState("")
  const [author, setAuthor] = useState("")
  const [version, setVersion] = useState("")
  const [repoUrl, setRepoUrl] = useState("")
  const [isPaid, setIsPaid] = useState<0 | 1>(0)
  const [price, setPrice] = useState("")
  const [publisherType, setPublisherType] = useState("")

  /* ── Upload state ── */
  const [archiveFile, setArchiveFile] = useState<File | null>(null)
  const [archiveError, setArchiveError] = useState("")
  const [uploading, setUploading] = useState(false)
  const [isParsing, setIsParsing] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const archiveInputRef = useRef<HTMLInputElement>(null)

  /* ── Submit state ── */
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  /* ── Archive upload result ── */
  const [archiveAccessUrl, setArchiveAccessUrl] = useState<string | null>(null)
  const [parsedResult, setParsedResult] = useState<SkillVersionResp | null>(null)
  const isEditMode = !!editSkill

  /* ── Dropdown state ── */
  const [categoryOpen, setCategoryOpen] = useState(false)
  const [publisherTypeOpen, setPublisherTypeOpen] = useState(false)
  const categoryRef = useRef<HTMLDivElement>(null)
  const publisherTypeRef = useRef<HTMLDivElement>(null)

  /* ── Entrance animation ── */
  useEffect(() => {
    if (!open) return
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [open])

  /* ── Lock body scroll ── */
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = prev }
  }, [open])

  /* ── Reset on close ── */
  useEffect(() => {
    if (!open) {
      setEntered(false)
      setSubmitted(false)
      reset()
      setName("")
      setDescription("")
      setCategory("")
      setTags([])
      setTagInput("")
      setSource("")
      setAuthor("")
      setVersion("")
      setRepoUrl("")
      setIsPaid(0)
      setPrice("")
      setPublisherType("")
      setArchiveFile(null)
      setArchiveError("")
      setSubmitError(null)
      setUploadProgress(0)
      setUploading(false)
      setIsParsing(false)
      setArchiveAccessUrl(null)
      setParsedResult(null)
    }
  }, [open, reset])

  useEffect(() => {
    if (!open || !editSkill) return
    setName(editSkill.name || "")
    setDescription(editSkill.description || editSkill.content || "")
    setCategory(editSkill.category || "")
    setTags(editSkill.tags ? editSkill.tags.split(",").map((t) => t.trim()).filter(Boolean) : [])
    setTagInput("")
    setSource(editSkill.source || "")
    setAuthor(editSkill.author || "")
    setVersion(editSkill.version || "")
    setRepoUrl(editSkill.repoUrl || "")
    setIsPaid(editSkill.isPaid === 1 ? 1 : 0)
    setPrice(editSkill.price != null ? String(editSkill.price) : "")
    setPublisherType(editSkill.publisherType || "")
    setArchiveFile(null)
    setArchiveError("")
    setSubmitError(null)
    setUploadProgress(0)
    setUploading(false)
    setIsParsing(false)
    setArchiveAccessUrl(editSkill.archivePath || null)
    setParsedResult(null)
  }, [open, editSkill])

  /* ── Close dropdowns on outside click ── */
  useEffect(() => {
    if (!categoryOpen && !publisherTypeOpen) return
    const handler = (e: MouseEvent) => {
      if (categoryOpen && categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryOpen(false)
      }
      if (publisherTypeOpen && publisherTypeRef.current && !publisherTypeRef.current.contains(e.target as Node)) {
        setPublisherTypeOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [categoryOpen, publisherTypeOpen])

  /* ── Archive file validation ── */
  const validateArchive = useCallback((f: File): string | null => {
    const name = f.name.toLowerCase()
    const isValid = ARCHIVE_ACCEPTED_TYPES.some((ext) =>
      ext === ".tar.gz" || ext === ".tar.bz2" || ext === ".tar.xz"
        ? name.endsWith(ext)
        : name.endsWith(ext)
    )
    if (!isValid) return `仅支持 ${ARCHIVE_ACCEPTED_TYPES.join("、")} 格式`
    if (f.size > ARCHIVE_MAX_SIZE) return "文件大小不能超过 2MB"
    return null
  }, [])

  /* ── Handle archive selection: 验证 → 上传 → 解析 → 反显 ── */
  const handleArchiveSelect = useCallback(async (f: File) => {
    const err = validateArchive(f)
    if (err) { setArchiveError(err); return }

    setArchiveError("")
    setArchiveFile(f)
    setArchiveAccessUrl(null)
    setParsedResult(null)
    setUploadProgress(0)
    setUploading(true)
    setIsParsing(false)

    try {
      // Step 1: 上传压缩包到 MinIO
      const accessUrl = await uploadFile(f)
      setArchiveAccessUrl(accessUrl)
      setUploading(false)
      analytics.track("SkillArchiveUploaded", {
        file_name: f.name,
        file_size: f.size,
      })

      // Step 2: 调用解析接口
      setIsParsing(true)
      const result = await parseSkillByUrl(accessUrl)
      setParsedResult(result)

      // Step 3: 反显解析内容到表单
      setName(result.name || "")
      setDescription(result.description || "")
      setCategory(result.category || "")
      setTags(result.tags ? result.tags.split(",").map((t) => t.trim()).filter(Boolean) : [])
      setSource(result.source || "")
      setVersion(result.version || "")
      setRepoUrl(result.repoUrl || "")

      toast.success("解析成功，请确认表单信息")
    } catch (err) {
      const msg = err instanceof Error ? err.message : "上传或解析失败，请稍后重试"
      setArchiveError(msg)
      setArchiveFile(null)
      setArchiveAccessUrl(null)
      setParsedResult(null)
      setUploadProgress(0)
    } finally {
      setUploading(false)
      setIsParsing(false)
    }
  }, [validateArchive, toast])

  const handleArchiveInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) handleArchiveSelect(f)
    e.target.value = ""
  }, [handleArchiveSelect])

  const handleArchiveDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const f = e.dataTransfer.files?.[0]
    if (f) handleArchiveSelect(f)
  }, [handleArchiveSelect])

  /* ── Submit handler ── */
  const handleSubmit = useCallback(async () => {
    if (!name.trim() || !description.trim()) return
    setSubmitting(true)
    setSubmitError(null)

    try {
      const req: CreateSkillRequest = {
        name: name.trim(),
        description: description.trim(),
        skillKey: parsedResult?.skillKey || undefined,
        category: category || undefined,
        tags: tags.length > 0 ? tags.join(",") : undefined,
        source: source || undefined,
        author: author || undefined,
        version: version || undefined,
        repoUrl: repoUrl || undefined,
        isPaid,
        price: isPaid === 1 && price ? parseFloat(price) : undefined,
        publisherType: publisherType || undefined,
        archivePath: archiveAccessUrl ?? undefined,
      }
      if (isEditMode) {
        await updateSkill({
          ...req,
          id: editSkill.id,
          skillKey: parsedResult?.skillKey || editSkill.skillKey || undefined,
          archivePath: archiveAccessUrl ?? editSkill.archivePath ?? undefined,
          content: description.trim(),
        })
      } else {
        await createSkill(req)
      }

      analytics.track(isEditMode ? "SkillUpdated" : "SkillPublished", {
        skill_name: req.name,
        skill_key: (parsedResult?.skillKey || editSkill?.skillKey || req.skillKey) ?? "",
        category: req.category ?? "",
        is_paid: req.isPaid,
        publisher_type: req.publisherType ?? "",
      })

      setSubmitted(true)
      setTimeout(() => {
        onSuccess?.()
        requestClose()
      }, 1500)
    } catch (err) {
      const msg = err instanceof Error ? err.message : "发布失败，请稍后重试"
      setSubmitError(msg)
    } finally {
      setSubmitting(false)
    }
  }, [name, description, category, tags, source, author, version, repoUrl, isPaid, price, publisherType, archiveAccessUrl, parsedResult, editSkill, isEditMode, requestClose, onSuccess])

  // 压缩包必传：需要解析成功才能发布
  const canSubmit = (isEditMode || !!parsedResult) && name.trim().length > 0 && description.trim().length > 0 && !submitting && !uploading && !isParsing

  if (!open) return null

  const overlayClass = closing
    ? "opacity-0 backdrop-blur-none"
    : entered
      ? "opacity-100 backdrop-blur-xl"
      : "opacity-0 backdrop-blur-none"

  const panelStyle: React.CSSProperties = {
    opacity: closing ? 0 : entered ? 1 : 0,
    transform: closing
      ? "scale(0.96) translateY(8px)"
      : entered
        ? "scale(1) translateY(0)"
        : "scale(0.92) translateY(12px)",
    filter: closing ? "blur(4px)" : entered ? "blur(0)" : "blur(6px)",
    transitionTimingFunction: closing
      ? "cubic-bezier(0.4, 0, 1, 1)"
      : "cubic-bezier(0.16, 1, 0.3, 1)",
  }

  const selectedCategory = CATEGORY_OPTIONS.find((o) => o.value === category)
  const selectedPublisherType = PUBLISHER_TYPE_OPTIONS.find((o) => o.value === publisherType)

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${overlayClass}`}
        style={{ zIndex: Z.MODAL_OVERLAY, pointerEvents: closing || !entered ? "none" : "auto" }}
        onClick={requestClose}
        aria-hidden
      />

      {/* Centered modal */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_CONTENT, pointerEvents: "none" }}
      >
        <div
          className="liquid-glass-panel relative w-full max-w-[600px] max-h-[88vh] overflow-hidden rounded-2xl flex flex-col transition-all duration-300"
          style={{ ...panelStyle, pointerEvents: "auto" }}
        >
          {/* ═══════ Header ═══════ */}
          <div className="flex items-center justify-between px-5 py-3 shrink-0 border-b border-border/10">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10">
                <Layers className="h-3.5 w-3.5 text-primary" />
              </div>
              <h2 className="text-sm font-semibold text-foreground">{isEditMode ? "编辑技能" : "发布技能"}</h2>
            </div>
            <button
              type="button"
              onClick={requestClose}
              className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-foreground/5 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {submitted ? (
            /* ═══════ Success state ═══════ */
            <div className="flex-1 flex flex-col items-center justify-center gap-4 py-16 relative overflow-hidden">
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-24 h-24 rounded-full bg-primary/5 animate-ping" style={{ animationDuration: "2s" }} />
                <div className="absolute bottom-1/3 right-1/4 w-16 h-16 rounded-full bg-primary/5 animate-ping" style={{ animationDuration: "2.5s", animationDelay: "0.3s" }} />
              </div>
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg shadow-primary/20">
                  <Check className="h-8 w-8 text-primary-foreground" strokeWidth={3} />
                </div>
              </div>
              <div className="text-center relative">
                <p className="text-sm font-bold text-foreground">发布成功！</p>
                <p className="text-xs text-muted-foreground/60 mt-1.5">窗口即将关闭…</p>
              </div>
            </div>
          ) : (
            /* ═══════ Form body ═══════ */
            <div className="flex-1 overflow-y-auto">
              <div className="px-5 pt-5 pb-4 space-y-4">

                {/* ── 上传压缩包区域 ── */}
                <div>
                  <label className="block text-xs font-medium text-foreground/70 mb-1.5">
                    上传 skill 压缩包
                    {!isEditMode && <span className="ml-1 text-destructive">*</span>}
                  </label>
                  <div
                    onDrop={handleArchiveDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => !uploading && !isParsing && archiveInputRef.current?.click()}
                    className={`relative flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-xl border-2 border-dashed transition-all cursor-pointer group ${archiveFile
                      ? "border-primary/40 bg-primary/[0.04]"
                      : "border-border/40 bg-secondary/20 hover:border-primary/30 hover:bg-secondary/30"
                      } ${(uploading || isParsing) ? "cursor-wait" : ""}`}
                  >
                    {uploading ? (
                      <>
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Upload className="h-4 w-4 text-primary animate-bounce" />
                        </div>
                        <p className="text-xs text-foreground/60">上传中…</p>
                        {/* Progress bar */}
                        <div className="w-full max-w-[200px] h-1 rounded-full bg-border/20 overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all duration-300 rounded-full"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                      </>
                    ) : isParsing ? (
                      <>
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <ScanSearch className="h-4 w-4 text-primary" />
                        </div>
                        <p className="text-xs text-foreground/60">解析中…</p>
                        <Loader2 className="h-4 w-4 text-primary animate-spin" />
                      </>
                    ) : archiveFile ? (
                      <>
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <FileArchive className="h-4 w-4 text-primary" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs font-medium text-foreground/80">{archiveFile.name}</p>
                          <p className="text-micro text-muted-foreground/50 mt-0.5">
                            {(archiveFile.size / 1024 / 1024).toFixed(2)} MB
                            {archiveAccessUrl && (
                              <span className="ml-2 text-primary">
                                {parsedResult ? "· 已解析" : "· 已上传"}
                              </span>
                            )}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setArchiveFile(null)
                            setArchiveAccessUrl(null)
                            setParsedResult(null)
                            setUploadProgress(0)
                          }}
                          className="text-micro text-muted-foreground/50 hover:text-destructive transition-colors underline"
                        >
                          移除
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-8 rounded-lg bg-foreground/5 flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                          <Upload className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-foreground/60 group-hover:text-foreground/80 transition-colors">
                            拖拽或点击上传压缩包
                          </p>
                          <p className="text-micro text-muted-foreground/40 mt-0.5">
                            支持 {ARCHIVE_ACCEPTED_TYPES.join("、")}，最大 2MB
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    ref={archiveInputRef}
                    type="file"
                    accept={ARCHIVE_ACCEPT_ATTR}
                    className="hidden"
                    onChange={handleArchiveInputChange}
                  />
                  {archiveError && (
                    <div className="flex items-center gap-1.5 mt-1.5 text-micro text-destructive">
                      <AlertCircle className="h-3 w-3 shrink-0" />
                      <span>{archiveError}</span>
                    </div>
                  )}
                </div>

                {/* ── Divider ── */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border/20" />
                  <span className="text-micro text-muted-foreground/40">解析结果</span>
                  <div className="flex-1 h-px bg-border/20" />
                </div>

                {/* ── Name ── */}
                <div>
                  <label className="block text-xs font-medium text-foreground/70 mb-1.5">
                    技能名称 <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="输入技能名称…"
                    maxLength={100}
                    className="w-full h-9 px-3 rounded-xl bg-secondary/30 border border-border/30 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50 focus:bg-secondary/40 transition-all"
                  />
                </div>

                {/* ── Description ── */}
                <div>
                  <label className="block text-xs font-medium text-foreground/70 mb-1.5">
                    描述 <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="简要描述这个技能的功能和用途…"
                    rows={5}
                    maxLength={500}
                    className="w-full px-3 py-2 rounded-xl bg-secondary/30 border border-border/30 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50 focus:bg-secondary/40 transition-all resize-none leading-relaxed"
                  />
                </div>

                {/* ── Version ── */}
                <div>
                  <label className="block text-xs font-medium text-foreground/70 mb-1.5">版本</label>
                  <input
                    type="text"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="如 1.0.0…"
                    className="w-full h-9 px-3 rounded-xl bg-secondary/30 border border-border/30 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50 focus:bg-secondary/40 transition-all"
                  />
                </div>

                {/* ── RepoUrl ── */}
                <div>
                  <label className="block text-xs font-medium text-foreground/70 mb-1.5">仓库地址</label>
                  <input
                    type="text"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                    placeholder="https://github.com/..."
                    className="w-full h-9 px-3 rounded-xl bg-secondary/30 border border-border/30 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50 focus:bg-secondary/40 transition-all"
                  />
                </div>

                {/* ── Directory preview ── */}
                {parsedResult?.directory && (
                  <div>
                    <label className="block text-xs font-medium text-foreground/70 mb-1.5">目录结构</label>
                    <pre className="w-full px-3 py-2.5 rounded-xl bg-secondary/20 border border-border/20 text-micro text-muted-foreground/70 font-mono leading-relaxed overflow-x-auto whitespace-pre">
                      {parsedResult.directory}
                    </pre>
                  </div>
                )}

                {/* ── Category + PublisherType (2-col) ── */}
                <div className="grid grid-cols-2 gap-3">
                  {/* Category dropdown */}
                  <div ref={categoryRef} className="relative">
                    <label className="block text-xs font-medium text-foreground/70 mb-1.5">分类</label>
                    <button
                      type="button"
                      onClick={() => setCategoryOpen((v) => !v)}
                      className="w-full flex items-center justify-between h-9 px-3 rounded-xl bg-secondary/30 border border-border/30 text-sm text-left transition-all hover:border-primary/30 focus:outline-none focus:border-primary/50"
                    >
                      <span className={selectedCategory ? "text-foreground" : "text-muted-foreground/40"}>
                        {selectedCategory ? selectedCategory.label : "选择分类"}
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground/60 transition-transform ${categoryOpen ? "rotate-180" : ""}`} />
                    </button>
                    {categoryOpen && (
                      <div className="absolute mt-1 w-48 rounded-xl border border-border/40 bg-popover/95 backdrop-blur-xl shadow-xl py-1 z-50">
                        {CATEGORY_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => { setCategory(opt.value); setCategoryOpen(false) }}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${category === opt.value
                              ? "text-primary bg-primary/10"
                              : "text-foreground/80 hover:bg-secondary/60"
                              }`}
                          >
                            {opt.label}
                            {category === opt.value && <Check className="h-3 w-3 ml-auto" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* PublisherType dropdown */}
                  <div ref={publisherTypeRef} className="relative">
                    <label className="block text-xs font-medium text-foreground/70 mb-1.5">发布者类型</label>
                    <button
                      type="button"
                      onClick={() => setPublisherTypeOpen((v) => !v)}
                      className="w-full flex items-center justify-between h-9 px-3 rounded-xl bg-secondary/30 border border-border/30 text-sm text-left transition-all hover:border-primary/30 focus:outline-none focus:border-primary/50"
                    >
                      <span className={selectedPublisherType ? "text-foreground" : "text-muted-foreground/40"}>
                        {selectedPublisherType ? selectedPublisherType.label : "选择类型"}
                      </span>
                      <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground/60 transition-transform ${publisherTypeOpen ? "rotate-180" : ""}`} />
                    </button>
                    {publisherTypeOpen && (
                      <div className="absolute mt-1 w-40 rounded-xl border border-border/40 bg-popover/95 backdrop-blur-xl shadow-xl py-1 z-50">
                        {PUBLISHER_TYPE_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => { setPublisherType(opt.value); setPublisherTypeOpen(false) }}
                            className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${publisherType === opt.value
                              ? "text-primary bg-primary/10"
                              : "text-foreground/80 hover:bg-secondary/60"
                              }`}
                          >
                            {opt.label}
                            {publisherType === opt.value && <Check className="h-3 w-3 ml-auto" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── isPaid toggle + price ── */}
                <div className="flex items-start gap-3">
                  {/* 免费/收费 toggle */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-foreground/70 mb-1.5">价格类型</label>
                    <div className="flex h-9 rounded-xl overflow-hidden border border-border/30 bg-secondary/30">
                      <button
                        type="button"
                        onClick={() => { setIsPaid(0); setPrice("") }}
                        className={`flex-1 text-xs font-medium transition-colors ${isPaid === 0
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                          }`}
                      >
                        免费
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsPaid(1)}
                        className={`flex-1 text-xs font-medium transition-colors ${isPaid === 1
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                          }`}
                      >
                        收费
                      </button>
                    </div>
                  </div>
                  {/* 价格输入（收费时才显示） */}
                  {isPaid === 1 && (
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-foreground/70 mb-1.5">价格（元）</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="0.00"
                        className="w-full h-9 px-3 rounded-xl bg-secondary/30 border border-border/30 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50 focus:bg-secondary/40 transition-all"
                      />
                    </div>
                  )}
                </div>

                {/* ── Tags ── */}
                <div>
                  <label className="block text-xs font-medium text-foreground/70 mb-1.5">标签</label>
                  <div className="flex flex-wrap gap-1.5 min-h-9 px-2.5 py-1.5 rounded-xl bg-secondary/30 border border-border/30 focus-within:border-primary/50 focus-within:bg-secondary/40 transition-all">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 h-6 px-2 rounded-lg bg-primary/10 text-primary text-xs font-medium"
                      >
                        {tag}
                        <button
                          type="button"
                          aria-label={`移除标签 ${tag}`}
                          onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                          className="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-primary/20 transition-colors"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
                          e.preventDefault()
                          const val = tagInput.trim().replace(/,$/, "")
                          if (val && !tags.includes(val)) setTags((prev) => [...prev, val])
                          setTagInput("")
                        } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
                          setTags((prev) => prev.slice(0, -1))
                        }
                      }}
                      onBlur={() => {
                        const val = tagInput.trim().replace(/,$/, "")
                        if (val && !tags.includes(val)) setTags((prev) => [...prev, val])
                        setTagInput("")
                      }}
                      placeholder={tags.length === 0 ? "输入后按 Enter 或逗号添加标签…" : ""}
                      className="flex-1 min-w-[120px] h-6 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none"
                    />
                  </div>
                </div>

                {/* ── Source ── */}
                <div>
                  <label className="block text-xs font-medium text-foreground/70 mb-1.5">来源</label>
                  <input
                    type="text"
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    placeholder="技能来源描述…"
                    className="w-full h-9 px-3 rounded-xl bg-secondary/30 border border-border/30 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/50 focus:bg-secondary/40 transition-all"
                  />
                </div>

                {/* ── Error ── */}
                {submitError && (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-destructive/8 border border-destructive/15 text-xs text-destructive">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══════ Footer ═══════ */}
          {!submitted && (
            <div className="flex items-center justify-between px-5 py-3 shrink-0 border-t border-border/10">
              <span className="text-micro text-muted-foreground/40">
                {isEditMode ? (
                  <span className="text-primary">编辑模式</span>
                ) : parsedResult ? (
                  <span className="text-primary">✓ 已解析压缩包</span>
                ) : archiveAccessUrl ? (
                  <span className="text-primary">✓ 压缩包已上传</span>
                ) : (
                  "* 为必填项"
                )}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={requestClose}
                  className="h-8 px-3.5 rounded-full text-xs text-muted-foreground hover:text-foreground hover:bg-foreground/5 transition-colors"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                  className="flex h-8 items-center gap-1.5 px-4 rounded-full text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="h-3 w-3 border border-primary-foreground/60 border-t-primary-foreground rounded-full animate-spin" />
                      {isEditMode ? "保存中" : "发布中"}
                    </>
                  ) : (
                    isEditMode ? "保存技能" : "发布技能"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  )
}
